/**
 * Assumptions Modal — country, currency, FX, financing, thresholds.
 * Backend-driven defaults via ConfigContext.
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { ClearableNumberInput } from './ClearableNumberInput';
import { GearSix } from '@phosphor-icons/react';
import { useConfig } from '../context/ConfigContext';
import {
  FALLBACK_COUNTRY_PRESETS,
  FALLBACK_CURRENCIES,
  applyCountryLocals,
  countryFlagEmoji,
  setCountryCurrencyOnly,
} from '../utils/countryPresets';

const COST_SECTIONS = [
  {
    title: 'Construction',
    fields: [
      { key: 'constructionPercent', label: 'Construction', unit: '% of Revenue', scale: 100 },
      { key: 'constructionReservePercent', label: 'Reserve', unit: '% of Construction', scale: 100 },
    ],
  },
  {
    title: 'Soft Costs',
    fields: [
      { key: 'architecturePercent', label: 'Architecture', unit: '% of Construction', scale: 100 },
      { key: 'consultantsPercent', label: 'Consultants', unit: '% of Construction', scale: 100 },
      { key: 'contingencyPercent', label: 'Contingency', unit: '% of Construction', scale: 100 },
    ],
  },
  {
    title: 'Land & Commercial',
    fields: [
      { key: 'notaryFeesPercent', label: 'Notary Fees', unit: '% of Land', scale: 100 },
      { key: 'otherLandFeesPercent', label: 'Other Land', unit: '% of Land', scale: 100 },
      { key: 'commercializationPercent', label: 'Sales Cost', unit: '% of Revenue', scale: 100 },
      { key: 'pilotagePercent', label: 'Pilotage', unit: '% of PBT', scale: 100 },
    ],
  },
];

const CAPITAL_SECTIONS = [
  {
    title: 'Capital Defaults',
    fields: [
      { key: 'defaultEquityPercent', label: 'Equity (you)', unit: '% of base', scale: 100 },
      { key: 'defaultBankDebtPercent', label: 'Bank Debt', unit: '% of base', scale: 100 },
      { key: 'defaultPrivateCapitalPercent', label: 'Partner / Private', unit: '% of base', scale: 100 },
      { key: 'defaultFeesPercent', label: 'Fees', unit: '% of base', scale: 100 },
      { key: 'equityStructuringFees', label: 'Eq. Struct. Fees', unit: '% of Equity', scale: 100 },
    ],
  },
  {
    title: 'Decision Thresholds',
    fields: [
      { key: 'roiGoThreshold', label: 'GO >=', unit: '% ROI', scale: 100 },
      { key: 'roiWatchThreshold', label: 'WATCH >=', unit: '% ROI', scale: 100 },
      { key: 'targetROI', label: 'Target ROI', unit: '% ROI', scale: 100 },
      { key: 'landToRevenueMaxRatio', label: 'Max Land/Rev', unit: '%', scale: 100 },
      { key: 'minMarginPercent', label: 'Min Margin', unit: '%', scale: 100 },
    ],
  },
  {
    title: 'Product',
    fields: [
      { key: 'avgUnitSize', label: 'Avg Unit Size', unit: 'm2', scale: 1 },
      { key: 'marketReferencePricePerSqm', label: 'Market Ref', unit: '/m2', scale: 1 },
    ],
  },
];

function Field({ label, value, onChange, unit, scale }) {
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <span className="text-[10px] text-gray-600 w-24 shrink-0">{label}</span>
      <ClearableNumberInput
        value={value || 0}
        onChange={onChange}
        scale={scale}
        aria-label={label}
        className="h-6 px-1.5 text-[10px] tabular-nums bg-gray-50 border-gray-200 rounded w-20"
      />
      <span className="text-[8px] text-gray-400 shrink-0">{unit}</span>
    </div>
  );
}

function NestedField({ label, value, onChange, unit, scale = 1, step }) {
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <span className="text-[10px] text-gray-600 w-28 shrink-0">{label}</span>
      <ClearableNumberInput
        value={value || 0}
        onChange={onChange}
        scale={scale}
        step={step}
        aria-label={label}
        className="h-6 px-1.5 text-[10px] tabular-nums bg-gray-50 border-gray-200 rounded w-20"
      />
      <span className="text-[8px] text-gray-400 shrink-0">{unit}</span>
    </div>
  );
}

export function AssumptionsModal({
  assumptions,
  onAssumptionsChange,
  fx,
  onRefreshFx,
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}) {
  const { assumptions: backendDefaults, countryPresets, operatingCurrencies } = useConfig();
  const presets = {
    ...FALLBACK_COUNTRY_PRESETS,
    ...((countryPresets && Object.keys(countryPresets).length) ? countryPresets : {}),
  };

  const [local, setLocal] = useState({ ...assumptions });
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const [countryFilter, setCountryFilter] = useState('');

  // Version switch replaces `assumptions`. Drop stale modal drafts so Apply
  // cannot write the previous version's rates onto the newly active one.
  useEffect(() => {
    setLocal({ ...assumptions });
  }, [assumptions]);

  const opCurForList = (local.operatingCurrency || assumptions.operatingCurrency || 'CHF').toUpperCase();
  const currencies = [...new Set([
    ...FALLBACK_CURRENCIES,
    ...((Array.isArray(operatingCurrencies) && operatingCurrencies.length) ? operatingCurrencies : []),
    opCurForList,
  ])].sort();

  const set = (key, value) => setLocal((prev) => ({ ...prev, [key]: value }));
  const setNested = (parent, key, value) => {
    setLocal((prev) => ({ ...prev, [parent]: { ...prev[parent], [key]: value } }));
  };

  const handleCountryChange = (code) => {
    setLocal((prev) => setCountryCurrencyOnly(prev, code, presets));
  };

  const handleApplyLocals = () => {
    setLocal((prev) => applyCountryLocals(prev, prev.countryCode || 'CH', presets));
  };

  const handleApply = () => {
    const next = {
      ...local,
      reportingCurrency: 'CHF',
      countryCode: (local.countryCode || 'CH').toUpperCase(),
      operatingCurrency: (local.operatingCurrency || 'CHF').toUpperCase(),
    };
    // Frozen without a snapshot must not break calculate — attach current FX if available.
    if ((next.fxMode || 'live') === 'frozen' && !(next.fxSnapshot && next.fxSnapshot.rate > 0)) {
      if (fx && fx.rate > 0) {
        next.fxSnapshot = {
          base: fx.base,
          quote: fx.quote,
          rate: fx.rate,
          asOf: fx.asOf,
          source: fx.source,
        };
      } else {
        next.fxMode = 'live';
        delete next.fxSnapshot;
      }
    }
    onAssumptionsChange(next);
    setOpen(false);
  };
  const handleReset = () => setLocal({ ...backendDefaults });
  const handleOpen = (isOpen) => {
    if (isOpen) {
      setLocal({ ...assumptions });
      setCountryFilter('');
    }
    if (!isControlled) setUncontrolledOpen(isOpen);
    onOpenChange?.(isOpen);
  };

  const bd = local.bankDebt || {};
  const pc = local.privateCapital || {};
  const countryCode = (local.countryCode || 'CH').toUpperCase();
  const opCur = (local.operatingCurrency || 'CHF').toUpperCase();
  const taxLabel = local.taxLabel || 'IBGI';
  const countryOptions = Object.entries(presets)
    .map(([code, p]) => ({
      code,
      label: p.label || code,
      flag: countryFlagEmoji(code),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const filterQ = countryFilter.trim().toLowerCase();
  const filteredCountryOptions = filterQ
    ? countryOptions.filter((o) => (
      o.label.toLowerCase().includes(filterQ)
      || o.code.toLowerCase().includes(filterQ)
    ))
    : countryOptions;
  const selectedOption = countryOptions.find((o) => o.code === countryCode)
    || { code: countryCode, label: countryCode, flag: countryFlagEmoji(countryCode) };
  // ALWAYS keep the full country list in <select> — filtering must never empty it
  // (typing a non-match used to remove every <option> and show a blank control).
  const selectOptions = countryOptions.length > 0
    ? countryOptions
    : [selectedOption];
  const filterHasHits = !filterQ || filteredCountryOptions.length > 0;
  const uniqueFilterHit = filterQ && filteredCountryOptions.length === 1
    ? filteredCountryOptions[0]
    : null;
  const topFilterHits = filterQ
    ? filteredCountryOptions.slice(0, 8)
    : [];

  const applyUniqueFilterHit = () => {
    if (uniqueFilterHit) {
      handleCountryChange(uniqueFilterHit.code);
      setCountryFilter('');
    }
  };

  const pickFilteredCountry = (code) => {
    handleCountryChange(code);
    setCountryFilter('');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      {!hideTrigger ? (
        <DialogTrigger asChild>
          <button
            className="flex items-center gap-0.5 text-[9px] text-gray-400 hover:text-gray-700 px-1.5 py-1.5 min-h-[28px] rounded hover:bg-gray-100 transition-colors"
            data-testid="assumptions-trigger" title="Settings" aria-label="Settings"
          >
            <GearSix weight="bold" className="w-3 h-3" />
          </button>
        </DialogTrigger>
      ) : null}
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto p-4" data-testid="assumptions-modal" aria-describedby="assumptions-desc">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Settings</DialogTitle>
          <p id="assumptions-desc" className="text-[9px] text-gray-400">Country, currency, financing, assumptions</p>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* ═══ COUNTRY & CURRENCY ═══ */}
          <div className="bg-emerald-50/60 border border-emerald-200 rounded p-2" data-testid="settings-country-currency">
            <div className="text-[8px] font-semibold text-emerald-700 uppercase tracking-wider mb-1">Country &amp; Currency</div>
            <div className="flex items-start gap-1.5 py-0.5">
              <span className="text-[10px] text-gray-600 w-28 shrink-0 pt-1">Country</span>
              <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                <div
                  className="flex items-center gap-1 text-[10px] font-semibold text-emerald-900"
                  data-testid="settings-country-selected"
                >
                  <span className="text-[14px] leading-none" aria-hidden="true">{selectedOption.flag}</span>
                  <span>{selectedOption.label} ({selectedOption.code})</span>
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyUniqueFilterHit();
                      }
                    }}
                    placeholder="Search country (e.g. Jamaica, Senegal)…"
                    className="h-6 px-1.5 text-[10px] flex-1"
                    data-testid="settings-country-filter"
                    aria-label="Search countries"
                  />
                  {countryFilter.trim() && (
                    <button
                      type="button"
                      className="text-[8px] text-emerald-800 underline shrink-0"
                      data-testid="settings-country-clear-filter"
                      onClick={() => setCountryFilter('')}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {topFilterHits.length > 0 && filterQ && (
                  <div className="flex flex-wrap gap-0.5" data-testid="settings-country-matches">
                    {topFilterHits.map((o) => (
                      <button
                        key={o.code}
                        type="button"
                        onClick={() => pickFilteredCountry(o.code)}
                        className={`text-[8px] px-1.5 py-0.5 rounded border ${
                          o.code === countryCode
                            ? 'bg-emerald-700 text-white border-emerald-700'
                            : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                        }`}
                      >
                        {o.flag} {o.label}
                      </button>
                    ))}
                  </div>
                )}
                {!filterHasHits && (
                  <div className="text-[8px] text-amber-700" data-testid="settings-country-no-match">
                    No country matches “{countryFilter.trim()}”. Clear search or scroll the list below.
                  </div>
                )}
                <select
                  value={countryCode}
                  onChange={(e) => { handleCountryChange(e.target.value); setCountryFilter(''); }}
                  size={10}
                  className="min-h-[140px] w-full px-1.5 py-1 text-[10px] bg-white border border-gray-200 rounded leading-tight"
                  data-testid="settings-country"
                  aria-label="Country list"
                >
                  {selectOptions.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.flag ? `${o.flag} ` : ''}{o.label} ({o.code})
                    </option>
                  ))}
                </select>
                <div className="text-[7px] text-emerald-700/80" data-testid="settings-country-count">
                  {selectOptions.length} countries · scroll or search above
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 py-0.5">
              <span className="text-[10px] text-gray-600 w-28 shrink-0">Operating</span>
              <select
                value={opCur}
                onChange={(e) => set('operatingCurrency', e.target.value)}
                className="h-6 px-1.5 text-[10px] bg-white border border-gray-200 rounded flex-1"
                data-testid="settings-operating-currency"
              >
                {currencies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
                {!currencies.includes(opCur) && (
                  <option value={opCur}>{opCur}</option>
                )}
              </select>
            </div>
            <div className="flex items-center gap-1.5 py-0.5">
              <span className="text-[10px] text-gray-600 w-28 shrink-0">Reporting</span>
              <span className="text-[10px] font-semibold text-gray-800" data-testid="settings-reporting-currency">CHF (locked)</span>
            </div>
            <div className="flex items-center gap-1.5 py-0.5">
              <span className="text-[10px] text-gray-600 w-28 shrink-0">FX mode</span>
              <div className="flex gap-px flex-wrap">
                {['live', 'frozen', 'manual'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => set('fxMode', m)}
                    className={`text-[9px] px-2 py-0.5 rounded font-medium transition-colors ${
                      (local.fxMode || 'live') === m
                        ? 'bg-emerald-700 text-white'
                        : 'bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                    }`}
                    data-testid={`fx-mode-${m}`}
                  >{m}</button>
                ))}
              </div>
            </div>
            {(local.fxMode || 'live') === 'manual' && (
              <NestedField
                label={`1 ${opCur} =`}
                value={local.manualFxRate || 0}
                onChange={(v) => set('manualFxRate', v)}
                unit="CHF"
                scale={1}
                step={0.001}
              />
            )}
            {fx && fx.rate != null && opCur !== 'CHF' && (
              <div className="text-[8px] text-emerald-700 mt-1 px-1" data-testid="settings-fx-status">
                Current: 1 {fx.base} = {Number(fx.rate).toFixed(4)} {fx.quote}
                {fx.asOf ? ` · ${fx.asOf}` : ''} · {fx.source}
                {fx.stale ? ' (stale)' : ''}
                {typeof onRefreshFx === 'function' && (
                  <button
                    type="button"
                    onClick={onRefreshFx}
                    className="ml-2 underline hover:no-underline"
                    data-testid="settings-refresh-fx"
                  >Refresh FX</button>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleApplyLocals}
              className="mt-1.5 text-[9px] text-emerald-800 bg-white border border-emerald-300 hover:bg-emerald-100 px-2 py-0.5 rounded"
              data-testid="apply-country-locals"
            >
              Apply suggested locals (tax, notary, market ref)
            </button>
            <div className="text-[8px] text-emerald-600 mt-1 px-1 leading-snug">
              Inputs stay in {opCur}. KPIs and P&amp;L always report in CHF.
              {countryCode !== 'CH' && ' Cadastre is Swiss-only.'}
            </div>
          </div>

          {/* ═══ BANK DEBT ═══ */}
          <div className="bg-blue-50/50 border border-blue-200 rounded p-2" data-testid="settings-bank-debt">
            <div className="text-[8px] font-semibold text-blue-700 uppercase tracking-wider mb-1">Bank Debt</div>
            <NestedField label="Duration" value={bd.durationMonths || 18} onChange={(v) => setNested('bankDebt', 'durationMonths', v)} unit="months" scale={1} step={1} />
            <NestedField label="Interest Rate" value={bd.interestRate || 0} onChange={(v) => setNested('bankDebt', 'interestRate', v)} unit="% / year" scale={100} />
            <NestedField label="Setup Fees" value={bd.setupFees || 0} onChange={(v) => setNested('bankDebt', 'setupFees', v)} unit="% of debt" scale={100} />
            <NestedField label="Construction Facility" value={bd.constructionFacility || 0} onChange={(v) => setNested('bankDebt', 'constructionFacility', Math.max(0, v))} unit={local.operatingCurrency || 'CHF'} scale={1} step={100000} />
          </div>

          {/* ═══ PRIVATE / PARTNER CAPITAL ═══ */}
          <div className="bg-purple-50/50 border border-purple-200 rounded p-2" data-testid="settings-private-capital">
            <div className="text-[8px] font-semibold text-purple-700 uppercase tracking-wider mb-1">
              {pc.type === 'equity' ? 'Equity Partner' : 'Private Capital'}
            </div>
            <div className="flex items-center gap-1.5 py-0.5 mb-1">
              <span className="text-[10px] text-gray-600 w-28 shrink-0">Type</span>
              <div className="flex gap-px">
                {[{ v: 'debt', l: 'Debt' }, { v: 'equity', l: 'Equity (Partner)' }].map(opt => (
                  <button key={opt.v}
                    onClick={() => setNested('privateCapital', 'type', opt.v)}
                    className={`text-[9px] px-2 py-0.5 rounded font-medium transition-colors ${
                      pc.type === opt.v
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-purple-600 border border-purple-200 hover:bg-purple-100'
                    }`}
                    data-testid={`pc-type-${opt.v}`}
                  >{opt.l}</button>
                ))}
              </div>
            </div>

            {pc.type === 'debt' ? (
              <>
                <div className="flex items-center gap-1.5 py-0.5 mb-0.5">
                  <span className="text-[10px] text-gray-600 w-28 shrink-0">Calc Method</span>
                  <div className="flex gap-px">
                    {[{ v: 'annualized', l: 'Annualized' }, { v: 'fixed_roi', l: 'Fixed ROI' }].map(opt => (
                      <button key={opt.v}
                        onClick={() => setNested('privateCapital', 'financingType', opt.v)}
                        className={`text-[8px] px-1.5 py-0.5 rounded font-medium transition-colors ${
                          pc.financingType === opt.v
                            ? 'bg-purple-500 text-white'
                            : 'bg-white text-purple-500 border border-purple-200 hover:bg-purple-50'
                        }`}
                      >{opt.l}</button>
                    ))}
                  </div>
                </div>
                {pc.financingType === 'annualized' ? (
                  <>
                    <NestedField label="Interest Rate" value={pc.interestRate || 0} onChange={(v) => setNested('privateCapital', 'interestRate', v)} unit="% / year" scale={100} />
                    <NestedField label="Duration" value={pc.durationMonths || 18} onChange={(v) => setNested('privateCapital', 'durationMonths', v)} unit="months" scale={1} step={1} />
                  </>
                ) : (
                  <>
                    <NestedField label="Fixed ROI" value={pc.fixedROI || 0} onChange={(v) => setNested('privateCapital', 'fixedROI', v)} unit="% of capital" scale={100} />
                    <div className="text-[8px] text-purple-600 px-1 mb-0.5 leading-snug">
                      Lump return on private capital for the deal — not annualized by duration.
                    </div>
                  </>
                )}
                <NestedField label="Fees" value={pc.fees || 0} onChange={(v) => setNested('privateCapital', 'fees', v)} unit="% of capital" scale={100} />
              </>
            ) : (
              <>
                <NestedField label="Profit Share" value={pc.profitShare || 0} onChange={(v) => setNested('privateCapital', 'profitShare', v)} unit="% of net" scale={100} />
                <div className="text-[8px] text-purple-600 mt-1 px-1 leading-snug space-y-0.5">
                  <div className="font-semibold">How to activate</div>
                  <div>1. Set the partner’s amount under <span className="font-semibold">Funding → Partner capital</span> (not Equity).</div>
                  <div>2. Profit Share is a <span className="font-semibold">flat % of net after tax &amp; financing</span>.</div>
                  <div className="italic text-purple-500">JV dilution / promote — not interest. Does not reduce PBT or {taxLabel}.</div>
                </div>
              </>
            )}
          </div>

          {/* ═══ COST SECTIONS ═══ */}
          {COST_SECTIONS.map((section) => (
            <div key={section.title}>
              <div className="text-[8px] font-semibold text-gray-400 uppercase tracking-wider mb-1 border-b border-gray-100 pb-0.5">{section.title}</div>
              {section.fields.map((f) => (
                <Field key={f.key} label={f.label} value={local[f.key]} onChange={(v) => set(f.key, v)} unit={f.unit} scale={f.scale} />
              ))}
            </div>
          ))}

          {/* Tax — label from taxLabel */}
          <div>
            <div className="text-[8px] font-semibold text-gray-400 uppercase tracking-wider mb-1 border-b border-gray-100 pb-0.5">Tax</div>
            <Field label={taxLabel} value={local.taxRate} onChange={(v) => set('taxRate', v)} unit="% of PBT" scale={100} />
            <div className="flex items-center gap-1.5 py-0.5">
              <span className="text-[10px] text-gray-600 w-24 shrink-0">Tax label</span>
              <Input
                type="text"
                value={taxLabel}
                onChange={(e) => set('taxLabel', e.target.value)}
                className="h-6 px-1.5 text-[10px] bg-gray-50 border-gray-200 rounded w-28"
                data-testid="settings-tax-label"
              />
            </div>
          </div>

          {/* ═══ CAPITAL & THRESHOLD SECTIONS ═══ */}
          {CAPITAL_SECTIONS.map((section) => (
            <div key={section.title}>
              <div className="text-[8px] font-semibold text-gray-400 uppercase tracking-wider mb-1 border-b border-gray-100 pb-0.5">{section.title}</div>
              {section.fields.map((f) => {
                const unit = f.key === 'marketReferencePricePerSqm'
                  ? `${opCur}/m2`
                  : f.unit;
                return (
                  <Field key={f.key} label={f.label} value={local[f.key]} onChange={(v) => set(f.key, v)} unit={unit} scale={f.scale} />
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-4 pt-3 border-t border-gray-200">
          <button onClick={handleReset} className="text-[10px] text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100" data-testid="assumptions-reset">
            Reset to Defaults
          </button>
          <div className="flex gap-2">
            <button onClick={() => setOpen(false)} className="text-[10px] text-gray-500 hover:text-gray-700 px-3 py-1 rounded hover:bg-gray-100">Cancel</button>
            <button onClick={handleApply} className="text-[10px] text-white bg-gray-900 hover:bg-gray-800 px-3 py-1 rounded font-medium" data-testid="assumptions-apply">Apply</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
